import { ArpMediaType, InternalArpMedia } from '@arpeggi/shared'
import { Instance, types } from 'mobx-state-tree'
import { AudioEngine } from '../../__legacy__/audio/engine/AudioEngine'
import { getSampleColor } from '../../__legacy__/components/platform/color'
import SharedAudioContext from '../../__legacy__/stores/SharedAudioContext'
import { fetchArpWaveform } from '../../__legacy__/stores/registry/RegistryUtils'
import { deserializeSongFromStorage } from '../../__legacy__/stores/song/SongStore'
import { deserializeStemFromStorage } from '../../__legacy__/stores/song/stem/StemUtils'
import { detailPageUrl } from '../../__legacy__/utils/arpMedia'
import { ForDirectExtend } from '../mobx'
import Attachment from './Attachment'
import User from './User'

const ArpeggiComposition = types.union(types.string, types.frozen())

const _TokenGate = types.model({
  contractAddress: types.string,
  chain: types.enumeration(['ethereum', 'polygon', 'optimism']),
  minTokenRangeInclusive: types.number,
  maxTokenRangeInclusive: types.number,
  hasTokenRange: types.boolean
})

interface TokenGateType extends ForDirectExtend<typeof _TokenGate> {}
const TokenGate: TokenGateType = _TokenGate

const _ArpMediaMetadata = types.model({
  title: types.string,
  mediaType: types.string,
  bpm: types.number,
  key: types.number,
  genres: types.array(types.string),
  tags: types.array(types.string),
  arpeggiComposition: types.maybe(ArpeggiComposition)
})

interface ArpMediaMetadataType extends ForDirectExtend<typeof _ArpMediaMetadata> {}
const ArpMediaMetadata: ArpMediaMetadataType = _ArpMediaMetadata

const _MintExtras = types.model({
  id: types.number,
  coverImage: types.maybeNull(Attachment)
})

interface MintExtrasType extends ForDirectExtend<typeof _MintExtras> {}
const MintExtras: MintExtrasType = _MintExtras

const _ArpMedia = types
  .model({
    id: types.identifierNumber,
    type: types.enumeration([ArpMediaType.SONG, ArpMediaType.STEM, ArpMediaType.SAMPLE]),
    contractAddress: types.string,
    dataUri: types.string,
    cachedMp3DataUri: types.maybeNull(types.string),
    cachedWavDataUri: types.maybeNull(types.string),
    metadata: ArpMediaMetadata,
    favoriteCount: types.optional(types.number, 0),
    playCount: types.maybe(types.number),
    remixCount: types.maybe(types.number),
    instrument: types.maybe(types.string),
    isFeatured: types.maybe(types.boolean),
    isBlocklist: types.boolean,
    version: types.number,
    mintExtras: types.maybeNull(MintExtras),
    tokenId: types.number,
    createdAt: types.string,
    updatedAt: types.string,
    durationMs: types.maybe(types.number),
    userId: types.maybe(types.number),
    isDefaultSample: types.maybe(types.boolean),
    mintedAt: types.string,
    user: User,
    subComponents: types.maybe(types.array(types.number)),
    tokenGate: types.maybe(TokenGate),
    access: types.maybe(types.enumeration(['standard', 'token-gate-v1']))
  })
  .actions((self) => ({
    // workaround for updating favorite count
    updateFavoriteCount(newValue: number) {
      self.favoriteCount = newValue
    }
  }))
  .extend((self) => {
    let _audioBuffer: AudioBuffer = null
    let _audioBufferPromise: Promise<AudioBuffer> = null
    let _waveformPromise: Promise<ArrayBuffer> = null

    const fetchWaveform = async (): Promise<ArrayBuffer> => {
      return await fetchArpWaveform(self as unknown as InternalArpMedia)
    }

    /**
     * Fetches this sample, if not already fetched, and returns an audioBuffer of the sample's data.
     * If audioEngine is set, this will also update the engine's Virtual File System so the sample
     * can be played by the audioEngine.
     */
    async function fetchAudioBuffer(
      renderer: 'core' | 'offline' = 'core',
      audioEngine: AudioEngine = null
    ): Promise<AudioBuffer> {
      if (!_audioBuffer) {
        if (_audioBufferPromise) return await _audioBufferPromise
        /**
         * fetchAudioBuffer can get called multiple times at once, so this caches the promise
         * so we don't make multiple axios calls or decode the audio multiple times for the same sample
         */
        let waveform: ArrayBuffer
        if (_waveformPromise) waveform = await _waveformPromise
        else {
          _waveformPromise = fetchWaveform()
          waveform = await _waveformPromise
          _waveformPromise = null
        }
        if (_audioBufferPromise) return await _audioBufferPromise
        _audioBufferPromise = SharedAudioContext.decodeAudioData(waveform)
        _audioBuffer = await _audioBufferPromise
        _audioBufferPromise = null
      }
      if (audioEngine) {
        audioEngine.updateVirtualFileSystem(renderer, `${self.id}`, _audioBuffer)
      }
      return _audioBuffer
    }

    ////////////////////////// Stem Functions ///////////////////

    /**
     * Returns the StemStore object for this IArpMedia
     * @returns A StemStore object -- this cannot be typed because of circular dependencies
     */
    function getStemStore(): any {
      if (self.type !== ArpMediaType.STEM) return null

      const stem = deserializeStemFromStorage(self.metadata.arpeggiComposition)
      if (!stem) return null
      stem.updateRemixedStemId(self.id)
      return stem
    }

    return {
      views: {
        get audioBuffer(): AudioBuffer {
          return _audioBuffer
        },
        get audioBufferIsLoaded(): boolean {
          return _audioBuffer ? true : false
        },
        get detailPageUrl(): string {
          return detailPageUrl(this)
        },
        get category(): string {
          return self.metadata.tags[0]
        },
        get tokenColor(): string | null {
          if (self.type === ArpMediaType.STEM) {
            return deserializeStemFromStorage(self.metadata.arpeggiComposition).tokenColor
          } else if (self.type === ArpMediaType.SONG) {
            return deserializeSongFromStorage(self.metadata.arpeggiComposition).tokenColor
          } else if (self.type === ArpMediaType.SAMPLE) {
            return getSampleColor(self.id)
          }
          return null
        },
        getAudioData() {
          if (!_audioBuffer) return null
          return { audioData: _audioBuffer }
        },
        asInternalArpMedia(): InternalArpMedia {
          return {
            ...self,
            artistEthAddress: self.user.walletAddress,
            index: self.id,
            subcomponents: self.subComponents
          }
        }
      },
      actions: { fetchAudioBuffer, getStemStore }
    }
  })

interface ArpMediaMobxType extends ForDirectExtend<typeof _ArpMedia> {}
const ArpMedia: ArpMediaMobxType = _ArpMedia

export interface IArpMedia extends ForDirectExtend<Instance<typeof ArpMedia>> {}

export default ArpMedia
